import {BrowserRouter, Route, Routes} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';

import {useAuth} from '~/store/hooks';
import {VerifyPin} from '~/features/Auth/VerifyPin';
import {appActions, appSelectors} from '~/store/slices';

import {
  NotFoundScene,
  LoginScene,
  DashboardScene,
  CreatePinScene,
  UserLookupScene,
  RewardCodeLookupScene,
  PollListScene,
  WeeklyChallengeListScene,
  QuestionMatchingEvaluationResponseScene,
  QuestionMatchingEvaluationScene,
  QuestionSampleSetsScene,
  QuestionSampleSetDetailScene,
  QuestionSampleSetDataScene,
  ReportsAskStatsScene,
  QuestionMatchesQualityScene,
  PremiumStatsScreen,
  DiscountCodesScreen,
  TrialCodesScene,
  ManageConfigScene,
  UserListLookupScene,
  MigrationRunsScene,
  MigrationRunDetailScene,
  ExtractsScene,
  PdfConsentSubmissionScene,
  ResetTeenInviteScene,
  ParentLookupScene,
  QuestionAutoMatchesScene,
  AiToolTracesScene,
  AiToolTraceDetailScene,
  SystemHealthScene,
} from './scenes';
import {PollDetailsScene} from './scenes/PollDetailsScene';
import {SideMenu} from './components/SideMenu';
import {Header} from './components';

export const SceneManager = () => {
  const {authStateKnown, authenticated, adminUser, pin, showPinConfirm} =
    useAuth();

  const dispatch = useDispatch();
  const isSideDrawerOpen = useSelector(appSelectors.getSideDrawerOpen);

  const onCloseSideDrawer = () => {
    dispatch(appActions.onSetSideDrawerOpen(false));
  };

  // TODO - add spinner
  if (!authStateKnown) {
    return null;
  }

  if (!authenticated) {
    return <LoginScene />;
  }

  // TODO - add spinner
  // this state is here so that if we boot the user out they go back to the login scene and the auth ui re-mounts
  if (!adminUser) {
    return (
      <div className="h-screen flex flex-col items-center justify-center">
        <span className="loading loading-dots loading-lg"></span>
        <p>Loading</p>
      </div>
    );
  }

  if (!pin) {
    return <CreatePinScene />;
  }

  return (
    <>
      <BrowserRouter>
        <div className={'drawer ' + (isSideDrawerOpen ? 'drawer-open' : '')}>
          <input type="checkbox" id="drawer-toggle" className="drawer-toggle" />
          <div className="drawer-content">
            <Header />
            <div>
              <Routes>
                <Route path="/" element={<DashboardScene />} />
                <Route path="/system/config" element={<ManageConfigScene />} />
                <Route path="/system/health" element={<SystemHealthScene />} />
                <Route
                  path="/reports/ask-stats"
                  element={<ReportsAskStatsScene />}
                />
                <Route
                  path="/reports/question-matches-quality"
                  element={<QuestionMatchesQualityScene />}
                />
                <Route
                  path="/reward-lookup"
                  element={<RewardCodeLookupScene />}
                />
                <Route path="/user-lookup" element={<UserLookupScene />} />
                <Route
                  path="/user-list-lookup"
                  element={<UserListLookupScene />}
                />
                <Route path="/polls" element={<PollListScene />} />
                <Route path="/polls/:pollId" element={<PollDetailsScene />} />
                <Route
                  path="/challenges/weekly"
                  element={<WeeklyChallengeListScene />}
                />
                <Route path="/premium-stats" element={<PremiumStatsScreen />} />
                <Route
                  path="/subscriptions/discount-codes"
                  element={<DiscountCodesScreen />}
                />
                <Route
                  path="/subscriptions/trial-codes"
                  element={<TrialCodesScene />}
                />
                <Route
                  path="/ai-support/question-sample-sets"
                  element={<QuestionSampleSetsScene />}
                />
                <Route
                  path="/ai-support/question-sample-sets/:questionSampleSetId"
                  element={<QuestionSampleSetDetailScene />}
                />
                <Route
                  path="/ai-support/question-sample-sets/:questionSampleSetId/data"
                  element={<QuestionSampleSetDataScene />}
                />
                <Route
                  path="/ai-support/question-sample-sets/:questionSampleSetId/evaluate"
                  element={<QuestionMatchingEvaluationScene />}
                />
                <Route
                  path="/ai-support/question-sample-sets/:questionSampleSetId/evaluate/:questionMatchingEvaluationId"
                  element={<QuestionMatchingEvaluationResponseScene />}
                />
                <Route
                  path="/ai-support/question-auto-matches"
                  element={<QuestionAutoMatchesScene />}
                />
                <Route
                  path="/ai-support/ai-tool-traces"
                  element={<AiToolTracesScene />}
                />
                <Route
                  path="/ai-support/ai-tool-traces/:traceId"
                  element={<AiToolTraceDetailScene />}
                />
                <Route
                  path="/system/migrations"
                  element={<MigrationRunsScene />}
                />
                <Route
                  path="/system/migrations/:runId"
                  element={<MigrationRunDetailScene />}
                />
                <Route path="/system/extracts" element={<ExtractsScene />} />
                <Route
                  path="/parent-pay/pdf-consent"
                  element={<PdfConsentSubmissionScene />}
                />
                <Route
                  path="/parent-pay/reset-teen-invite"
                  element={<ResetTeenInviteScene />}
                />
                <Route
                  path="/parent-pay/parent-lookup"
                  element={<ParentLookupScene />}
                />
                <Route path="*" element={<NotFoundScene />} />
              </Routes>
            </div>
          </div>
          <div className="drawer-side">
            <SideMenu onCloseDrawer={onCloseSideDrawer} />
          </div>
        </div>
      </BrowserRouter>

      {showPinConfirm && (
        <dialog className="modal modal-open">
          <div className="modal-box">
            <VerifyPin />
          </div>
        </dialog>
      )}
    </>
  );
};
